import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { Fragment, useEffect, useState } from "react";
import { TableRow } from "./components";
import ChildTable from "./components/ChildTable";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import TextInputBase from "../Inputs/TextInputBase";
import useRouterHandlerHook from "../_hooks/useRouterHandlerHook";

interface ITableProps {
  parentTableData: Record<string, any>[];
  childTableData: Record<string, any>[];
  columnsSchemaParent: ColumnDef<any>[];
  columnsSchemaChild: ColumnDef<any>[];
  columnsSchema?: ColumnDef<any>[];
  tableEmptyComponent?: React.ReactNode;
  borderColor?: string;
  isHeaderSticky?: boolean;
  cellClassName?: string;
  onClickKey: string;
  query?: string;
  setQuery?: (arg: string) => void;
  searchPlaceHolder?: string;
  tableLabel?: string;
  setSelectedInsurerId: (id: string) => void;
  isLoadingChildTable: boolean;
}

function NestedTable({
  parentTableData,
  childTableData,
  isLoadingChildTable,
  setSelectedInsurerId,
  columnsSchemaParent,
  columnsSchemaChild,
  tableEmptyComponent,
  borderColor = "border-extraLightGray",
  isHeaderSticky = false,
  cellClassName = "",
  onClickKey = "",
  query,
  setQuery,
  searchPlaceHolder = "Type to search...",
  tableLabel,
}: ITableProps) {
  //states
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterState, setFilterState] = useState("");
  // custome hoooks
  const { urlQueries, navigateBackTo, navigateTo } = useRouterHandlerHook();
  const table = useReactTable({
    data: parentTableData,
    columns: columnsSchemaParent,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter: filterState.trim(),
    },
  });

  // HANDLER/s
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/\s+/g, " ");
    value = value.replace(/^\s/, "");
    if (query === undefined) {
      setFilterState(value);
    } else {
      setQuery?.(value);
    }
    navigateTo({
      search: {
        "t-search": value,
      },
    });
  };

  const toggleRowData = (id: string) => {
    setExpandedId((currentId) => (currentId === id ? null : id));
  };
  // EFFECT/s
  useEffect(() => {
    const tSearchText = urlQueries?.["t-search"];
    if (tSearchText) {
      if (query === undefined) {
        setFilterState(tSearchText);
      } else {
        setQuery?.(tSearchText);
      }
    }
  }, []);

  return (
    <div className="w-full">
      <div className="flex justify-between">
        <div>
          {tableLabel && (
            <h1 className="text-base font-semibold text-slate-900">
              {tableLabel}
            </h1>
          )}
        </div>
        <div className="w-56 mb-3">
          <TextInputBase
            type="text"
            value={query || filterState}
            onChange={handleChange}
            placeholder={searchPlaceHolder}
            className="h-9 pl-3 pr-10 text-sm text-body "
            icon={
              <>
                {query || filterState ? (
                  <button
                    type="button"
                    className="flex items-center rounded-md justify-center bg-primary/10 text-primary p-0.5"
                    onClick={() => {
                      setFilterState("");
                      setQuery?.("");
                      navigateBackTo({
                        searchKey: `t-search`,
                      });
                    }}
                  >
                    <XMarkIcon className=" size-4" />
                  </button>
                ) : (
                  <MagnifyingGlassIcon className="size-4" />
                )}
              </>
            }
          />
        </div>
      </div>
      <table className="w-full">
        <thead className="h-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              className={`bg-offWhite ${
                isHeaderSticky ? "sticky top-0 z-20" : ""
              }`}
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => (
                <th
                  className={`font-semibold text-subHeading border ${borderColor}
                    px-2 text-sm text-left border-t-0 first:border-l-0
                    last:border-r-0 last:text-right z-10`}
                  key={header.id}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => {
            const rowData = row.original;
            const isExpanded = expandedId === String(rowData.id);

            return (
              <Fragment key={row.id}>
                <TableRow>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`border ${borderColor} ${cellClassName}
                        first:border-l-0 last:border-r-0 text-sm`}
                    >
                      {cell.column.id === onClickKey ? (
                        <button
                          type="button"
                          onClick={() => {
                            toggleRowData(String(rowData.id));
                            setSelectedInsurerId(String(rowData?.id));
                          }}
                          aria-expanded={isExpanded}
                          className="font-semibold text-[#07566b] text-left
                            hover:underline cursor-pointer"
                        >
                          {rowData?.[onClickKey]}
                        </button>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      )}
                    </td>
                  ))}
                </TableRow>

                {isExpanded && (
                  <tr>
                    <td
                      colSpan={row.getVisibleCells().length}
                      className={`border ${borderColor} bg-slate-50 p-0`}
                    >
                      <ChildTable
                        childTableData={childTableData}
                        isLoadingChildTable={isLoadingChildTable}
                        columnsSchema={columnsSchemaChild}
                        tableEmptyComponent={tableEmptyComponent}
                        borderColor={borderColor}
                        cellClassName={cellClassName}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {table.getRowModel().rows.length === 0 && (
        <div className="flex items-center justify-center h-10 border-b border-extraLightGray rounded-lg">
          {tableEmptyComponent || (
            <p className="font-medium text-sm">No data available to show!</p>
          )}
        </div>
      )}
    </div>
  );
}

export default NestedTable;
